import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import AdminLayout from './AdminLayout';
import './Revenue.css';

const Revenue = () => {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => { fetchAnalytics(); }, []);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/revenue-analytics');
      setData(res);
      setError(null);
    } catch {
      setError('Failed to load revenue analytics.');
    } finally {
      setLoading(false);
    }
  };

  const fmtP = (v) => `£${((parseFloat(v) || 0) / 100).toFixed(2)}`;

  const pct = (a, b) => {
    const nb = parseFloat(b) || 0;
    return nb ? (((parseFloat(a) - nb) / nb) * 100).toFixed(1) : null;
  };
  const growth = data?.totals ? pct(
    data.totals.last_30_days,
    data.totals.prev_30_days
  ) : null;

  const maxRevenue = data?.monthly?.length
    ? Math.max(...data.monthly.map(m => m.revenue || 0), 1)
    : 1;

  if (loading) return (
    <AdminLayout pageTitle="Revenue">
      <div className="rv-page">
        <div className="rv-loading">
          <div className="rv-spinner" />
          <p>Loading revenue analytics…</p>
        </div>
      </div>
    </AdminLayout>
  );

  return (
    <AdminLayout pageTitle="Revenue">
      <div className="rv-page">
        {/* Header */}
        <div className="rv-header">
          <div className="rv-header-left">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
              <polyline points="17 6 23 6 23 12"/>
            </svg>
            <div>
              <h1 className="rv-title">Revenue Overview</h1>
              <p className="rv-subtitle">Business trends &amp; financial performance</p>
            </div>
          </div>
          <button className="rv-refresh-btn" onClick={fetchAnalytics}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
            </svg>
            Refresh
          </button>
        </div>

        {error && <div className="rv-alert">{error}</div>}

        {/* KPI Row */}
        <div className="rv-kpis">
          <div className="rv-kpi rv-kpi--primary">
            <div className="rv-kpi-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="1" x2="12" y2="23"/>
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
              </svg>
            </div>
            <div className="rv-kpi-body">
              <span className="rv-kpi-label">Net Revenue</span>
              <span className="rv-kpi-value">{fmtP(data?.totals?.total_revenue)}</span>
              <span className="rv-kpi-sub">after refunds &amp; referral payouts</span>
            </div>
          </div>

          <div className="rv-kpi rv-kpi--red">
            <div className="rv-kpi-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                <polyline points="9 22 9 12 15 12 15 22"/>
              </svg>
            </div>
            <div className="rv-kpi-body">
              <span className="rv-kpi-label">Total Refunded</span>
              <span className="rv-kpi-value rv-val--down">{fmtP(data?.refunds?.stats?.total_amount)}</span>
              <span className="rv-kpi-sub">{data?.refunds?.stats?.total_count || 0} refund{data?.refunds?.stats?.total_count !== 1 ? 's' : ''} processed</span>
            </div>
          </div>

          <div className="rv-kpi rv-kpi--orange">
            <div className="rv-kpi-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <line x1="23" y1="11" x2="17" y2="11"/>
              </svg>
            </div>
            <div className="rv-kpi-body">
              <span className="rv-kpi-label">Referral Payouts</span>
              <span className="rv-kpi-value rv-val--orange">{fmtP(data?.referrals?.stats?.total_paid_out)}</span>
              <span className="rv-kpi-sub">{data?.referrals?.stats?.paid_count || 0} paid · {data?.referrals?.stats?.pending_count || 0} pending</span>
            </div>
          </div>

          <div className="rv-kpi rv-kpi--green">
            <div className="rv-kpi-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/>
                <polyline points="16 7 22 7 22 13"/>
              </svg>
            </div>
            <div className="rv-kpi-body">
              <span className="rv-kpi-label">Last 30 Days</span>
              <span className="rv-kpi-value">{fmtP(data?.totals?.last_30_days)}</span>
              {growth !== null && (
                <span className={`rv-kpi-trend ${parseFloat(growth) >= 0 ? 'rv-kpi-trend--up' : 'rv-kpi-trend--down'}`}>
                  {parseFloat(growth) >= 0 ? '▲' : '▼'} {Math.abs(growth)}% vs prev 30 days
                </span>
              )}
            </div>
          </div>

          <div className="rv-kpi rv-kpi--blue">
            <div className="rv-kpi-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
                <line x1="8" y1="21" x2="16" y2="21"/>
                <line x1="12" y1="17" x2="12" y2="21"/>
              </svg>
            </div>
            <div className="rv-kpi-body">
              <span className="rv-kpi-label">Avg Transaction</span>
              <span className="rv-kpi-value">{fmtP(data?.totals?.avg_transaction)}</span>
              <span className="rv-kpi-sub">per successful payment</span>
            </div>
          </div>

          <div className="rv-kpi rv-kpi--purple">
            <div className="rv-kpi-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
            </div>
            <div className="rv-kpi-body">
              <span className="rv-kpi-label">Student Bookings</span>
              <span className="rv-kpi-value">{data?.bookings_total || 0}</span>
              <span className="rv-kpi-sub">total requests received</span>
            </div>
          </div>
        </div>

        <div className="rv-grid">
          {/* Monthly Revenue Bar Chart */}
          <div className="rv-card rv-card--chart">
            <div className="rv-card-header">
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="20" x2="18" y2="10"/>
                <line x1="12" y1="20" x2="12" y2="4"/>
                <line x1="6" y1="20" x2="6" y2="14"/>
              </svg>
              Monthly Revenue — Last 12 Months
            </div>
            <div className="rv-chart">
              {data?.monthly?.length > 0 ? data.monthly.map((m) => {
                const h = Math.round(((m.revenue || 0) / maxRevenue) * 100);
                return (
                  <div key={m.month_key} className="rv-bar-col">
                    <div className="rv-bar-tooltip">
                      <strong>{m.month_label}</strong>
                      <span>{fmtP(m.revenue)}</span>
                      <span>{m.transactions} sale{m.transactions !== 1 ? 's' : ''}</span>
                    </div>
                    <div className="rv-bar" style={{ height: `${Math.max(h, 2)}%` }} />
                    <span className="rv-bar-label">{(m.month_label || '').split(' ')[0]}</span>
                  </div>
                );
              }) : <div className="rv-chart-empty">No data yet — revenue will appear here once payments are processed</div>}
            </div>
          </div>

          {/* Package Breakdown */}
          <div className="rv-card">
            <div className="rv-card-header">
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
              </svg>
              Revenue by Package
            </div>
            {data?.packages?.length > 0 ? (
              <div className="rv-packages">
                {data.packages.map((pkg, i) => {
                  const total = data.packages.reduce((s, p) => s + (p.revenue || 0), 0) || 1;
                  const w = Math.round(((pkg.revenue || 0) / total) * 100);
                  return (
                    <div key={i} className="rv-pkg-row">
                      <div className="rv-pkg-meta">
                        <span className="rv-pkg-name">{pkg.package_name}</span>
                        <span className="rv-pkg-amt">{fmtP(pkg.revenue)}</span>
                      </div>
                      <div className="rv-pkg-bar-track">
                        <div className="rv-pkg-bar-fill" style={{ width: `${w}%` }} />
                      </div>
                      <span className="rv-pkg-count">{pkg.count} sale{pkg.count !== 1 ? 's' : ''}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="rv-empty-state">Package breakdown will appear once payments are recorded</div>
            )}
          </div>

          {/* Business Summary */}
          <div className="rv-card rv-card--summary">
            <div className="rv-card-header">
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
              </svg>
              Business Summary
            </div>
            <div className="rv-summary-rows">
              {[
                { label: 'Gross Revenue',      value: fmtP((data?.totals?.total_revenue || 0) + (data?.refunds?.stats?.total_amount || 0) + (data?.referrals?.stats?.total_paid_out || 0)), cls: '' },
                { label: 'Total Refunded',     value: `− ${fmtP(data?.refunds?.stats?.total_amount)}`, cls: 'rv-val--down' },
                { label: 'Referral Payouts',   value: `− ${fmtP(data?.referrals?.stats?.total_paid_out)}`, cls: 'rv-val--orange' },
                { label: 'Net Revenue',        value: fmtP(data?.totals?.total_revenue), cls: 'rv-sumrow-value--primary' },
                { label: 'Last 30 Days (net)', value: fmtP(data?.totals?.last_30_days) },
                { label: 'Previous 30 Days',   value: fmtP(data?.totals?.prev_30_days) },
                { label: 'Month-on-Month',     value: growth !== null ? `${parseFloat(growth) >= 0 ? '+' : ''}${growth}%` : '—',
                  cls: growth !== null ? (parseFloat(growth) >= 0 ? 'rv-val--up' : 'rv-val--down') : '' },
                { label: 'Total Transactions', value: data?.totals?.total_transactions || 0 },
                { label: 'Average Sale',       value: fmtP(data?.totals?.avg_transaction) },
                { label: 'Refunds Processed',  value: data?.refunds?.stats?.total_count || 0 },
                { label: 'Referrals Paid',     value: data?.referrals?.stats?.paid_count || 0 },
                { label: 'Referrals Pending',  value: data?.referrals?.stats?.pending_count || 0 },
                { label: 'Total Bookings',     value: data?.bookings_total || 0 },
                { label: 'Conversion Rate',
                  value: data?.bookings_total && data?.totals?.total_paid
                    ? `${Math.round((data.totals.total_paid / data.bookings_total) * 100)}%`
                    : '—' },
              ].map(({ label, value, cls }) => (
                <div className="rv-sumrow" key={label}>
                  <span className="rv-sumrow-label">{label}</span>
                  <span className={`rv-sumrow-value ${cls || ''}`}>{value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Refunds Breakdown */}
          <div className="rv-card rv-card--refunds">
            <div className="rv-card-header rv-card-header--red">
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                <polyline points="9 22 9 12 15 12 15 22"/>
              </svg>
              Refunds — Last 6 Processed
            </div>
            {data?.refunds?.recent?.length > 0 ? (
              <div className="rv-payout-list">
                {data.refunds.recent.map((r) => (
                  <div key={r.id} className="rv-payout-row rv-payout-row--refund">
                    <div className="rv-payout-info">
                      <span className="rv-payout-name">{r.student_name || 'Unknown'}</span>
                      <span className="rv-payout-meta">{r.package_type || '—'} · {r.reason ? r.reason.slice(0, 40) + (r.reason.length > 40 ? '…' : '') : 'No reason'}</span>
                    </div>
                    <div className="rv-payout-right">
                      <span className="rv-payout-amount rv-val--down">−{fmtP(r.amount_pence)}</span>
                      <span className="rv-payout-date">{r.completed_at ? new Date(r.completed_at).toLocaleDateString('en-GB') : '—'}</span>
                    </div>
                  </div>
                ))}
                <div className="rv-payout-total">
                  <span>Total Refunded</span>
                  <span className="rv-val--down">−{fmtP(data.refunds.stats.total_amount)}</span>
                </div>
              </div>
            ) : (
              <div className="rv-empty-state">No completed refunds yet</div>
            )}
          </div>

          {/* Referral Payouts Breakdown */}
          <div className="rv-card rv-card--referrals">
            <div className="rv-card-header rv-card-header--orange">
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <line x1="23" y1="11" x2="17" y2="11"/>
              </svg>
              Referral Payouts — Last 6 Paid
            </div>
            {data?.referrals?.recent?.length > 0 ? (
              <div className="rv-payout-list">
                {data.referrals.recent.map((r) => (
                  <div key={r.id} className="rv-payout-row rv-payout-row--referral">
                    <div className="rv-payout-info">
                      <span className="rv-payout-name">{r.instructor_name}</span>
                      <span className="rv-payout-meta">Referred: {r.referred_name}{r.payment_reference ? ` · Ref: ${r.payment_reference}` : ''}</span>
                    </div>
                    <div className="rv-payout-right">
                      <span className="rv-payout-amount rv-val--orange">−{fmtP(r.amount_pence)}</span>
                      <span className="rv-payout-date">{r.paid_at ? new Date(r.paid_at).toLocaleDateString('en-GB') : '—'}</span>
                    </div>
                  </div>
                ))}
                <div className="rv-payout-total">
                  <span>Total Paid Out</span>
                  <span className="rv-val--orange">−{fmtP(data.referrals.stats.total_paid_out)}</span>
                </div>
              </div>
            ) : (
              <div className="rv-empty-state">No referral payouts recorded yet</div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default Revenue;
